import { InsuranceConnectInline } from "@/components/InsuranceConnector/InsuranceConnectInline";
import { BoutiqueIntroComponent } from "@frontend/components/boutiqueIntro/BoutiqueIntro";
import { BoutqiueTransitionComponent } from "@frontend/components/boutqiueTransition/boutqiueTransition";
import CategoryList from "@frontend/components/category-list/CategoryList";
import { CategoryListClient } from "@frontend/components/category-list/CategoryListClient";
import CustomCarouselWrapper from "@frontend/components/customCarousel/CustomCarouselWrapper";
import FeaturedCarouselWrapper from "@frontend/components/featuredCarousel/FeaturedCarouselWrapper";
import { FeaturedCarouselWrapperClient } from "@frontend/components/featuredCarousel/FeaturedCarouselWrapperClient";
import { ProductCardCarouselWrapperClient } from "@frontend/components/productCardCarousel/ProductCardCarouselWrapperClient";
import { QuestionnaireComponent } from "@frontend/components/questionnaire/QuestionnaireComponent";
import { ShopNowButtonComponent } from "@frontend/components/shop-now";
import { AccordionContent } from "@frontend/shared/components/ui/accordion";
import {
  AccordionItem,
  AccordionTrigger,
} from "@frontend/shared/components/ui/accordion";
import { Accordion } from "@frontend/shared/components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@frontend/shared/components/ui/tabs";
import { cn } from "@frontend/shared/lib/utils";
import { GoogleMapsEmbed } from "@next/third-parties/google";
import Image from "next/image";
import Link from "next/link";
import type {
  Component,
  GoogleMapsEmbedProps,
  ImageProps,
  LinkProps,
  TextProps,
  VideoProps,
} from "../[clientSlug]/types";
import { ArrowDownCustomComponent } from "./ArrowDownCustomComponent";
import { CardCarouselComponent } from "./CardCarouselComponent";
import { ComplexFormComponent } from "./form-components/ComplexFormComponent";
import { InputComponent } from "./form-components/InputComponent";
import { LabelComponent } from "./form-components/LabelComponent";
import { SubmitButtonComponent } from "./form-components/SubmitButtonComponent";

export function ImageComponent({
  style,
  props,
}: {
  style: string;
  props: ImageProps;
}) {
  return (
    <div key={props.url} className={style}>
      {props.url && (
        <Image
          loading="lazy"
          src={props.url}
          alt={props.alt ?? ""}
          width={1800}
          height={1800}
          className="w-full h-full object-cover bg-clip-content overflow-clip"
        />
      )}
    </div>
  );
}

export function TextComponent({
  style,
  props,
}: {
  style: string;
  props: TextProps;
}) {
  return <div className={style}>{props.text}</div>;
}

function AutoPlayVideoComponent({
  style,
  props,
}: {
  style: string;
  props: VideoProps;
}) {
  return (
    <video autoPlay muted loop playsInline className={cn(style)} preload="none">
      <source src={props.videoSrc} type="video/mp4" />
    </video>
  );
}

function VideoWithControlsComponent({
  style,
  props,
}: {
  style: string;
  props: VideoProps;
}) {
  return (
    <video controls className={cn(style)} preload="none">
      <source src={props.videoSrc} type="video/mp4" />
      <track kind="captions" />
    </video>
  );
}

function LinkComponent({ style, props }: { style: string; props: LinkProps }) {
  return (
    <Link className={style} href={props.url ?? "#"}>
      {props.text}
    </Link>
  );
}

function GoogleMapsEmbedComponent({
  style,
  props,
}: {
  style: string;
  props: GoogleMapsEmbedProps;
}) {
  const { width, height, center, zoom, mode, styles, q } = props;

  return (
    <div className={style}>
      <GoogleMapsEmbed
        apiKey={process.env.NEXT_PUBLIC_GCP_MAPS_API as string}
        width={width ?? 600}
        height={height ?? 400}
        zoom={zoom ?? "15"}
        mode={mode ?? "place"}
        style={styles ?? "border-radius: 10px; "}
        q={q}
      />
    </div>
  );
}

export function renderChildren(
  children: Component[],
  params: { clientSlug: string; orgId: number },
  isClient = false,
) {
  return children.map((child) => renderComponent(child, params, isClient));
}

interface TabProps {
  image?: ImageProps;
  title?: string;
  tabs: {
    label: string;
    content: Component[];
  }[];
}

interface AccordionProps {
  items: {
    label: string;
    content: Component[];
  }[];
  multiple?: boolean;
  collapsible?: boolean;
}

function TabComponent({
  style,
  props,
  params,
}: {
  style: string;
  props: TabProps;
  params: { clientSlug: string; orgId: number };
}) {
  return (
    <div className={style}>
      <div className="flex gap-6">
        <div className={"flex-1 w-full"}>
          {props.title && (
            <div className="config-h3 font-bold">{props.title}</div>
          )}
          <Tabs defaultValue={props.tabs[0]?.label} className="w-full ">
            <TabsList className="bg-transparent w-fit flex flex-wrap justify-start p-0 m-0 mt-2">
              {props.tabs.map((tab) => (
                <TabsTrigger
                  key={tab.label}
                  value={tab.label}
                  className="border-b-2 w-fit border-transparent bg-transparent hover:border-b-gray-300 hover:fade-in-50 rounded-none  text-[#6D757F]  data-[state=active]:text-black data-[state=active]:border-b-black data-[state=active]:bg-transparent"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {props.tabs.map((tab) => (
              <TabsContent key={tab.label} value={tab.label}>
                {renderChildren(tab.content, {
                  clientSlug: params.clientSlug,
                  orgId: params.orgId,
                })}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export function AccordionComponent({
  style,
  props,
  params,
}: {
  style: string;
  props: AccordionProps;
  params: { clientSlug: string; orgId: number };
}) {
  if (!props.items?.length) return null;

  const accordionType = props.multiple ? "multiple" : "single";

  return (
    <Accordion type={accordionType} className={style}>
      {props.items.map((item) => (
        <AccordionItem key={item.label} value={item.label}>
          <AccordionTrigger className="text-left config-h4 font-normal">
            {item.label}
          </AccordionTrigger>
          <AccordionContent>
            {item.content?.map((component) =>
              renderComponent(component, params),
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function renderComponent(
  component: Component,
  params: { clientSlug: string; orgId: number; kioskMode?: boolean },
  isClient = false,
) {
  const isExperityBooking = params.orgId === 24;
  const { type, style, id } = component;
  const children =
    typeof component === "object" &&
    component !== null &&
    "children" in component
      ? component.children
      : undefined;

  switch (type) {
    case "video":
      return (
        <AutoPlayVideoComponent
          style={style}
          props={component.props}
          key={id}
        />
      );
    case "video-with-controls":
      return (
        <VideoWithControlsComponent
          style={style}
          props={component.props}
          key={id}
        />
      );
    case "text":
      return <TextComponent style={style} props={component.props} key={id} />;
    case "image":
      return <ImageComponent style={style} props={component.props} key={id} />;
    case "link":
      return <LinkComponent style={style} props={component.props} key={id} />;
    case "carousel":
      return isClient || isExperityBooking ? ( // this is temporary until we can properly add this to every org
        <FeaturedCarouselWrapperClient
          key={id}
          params={params}
          props={component.props}
        />
      ) : (
        <FeaturedCarouselWrapper
          key={id}
          params={params}
          props={component.props}
        />
      );
    case "category-list":
      return isClient ? (
        <CategoryListClient key={id} params={params} props={component.props} />
      ) : (
        <CategoryList key={id} params={params} props={component.props} />
      );
    case "insuranceStepper":
      return (
        <InsuranceConnectInline
          key="insurance-connector"
          contentOverrides={{
            eligibilityScreenContent: component.props?.eligibilityScreenContent,
            introScreenContent: component.props?.introScreenContent,
            unverifiedFinalStateContent:
              component.props?.unverifiedFinalStateContent,
          }}
        />
      );
    case "tabs":
      return (
        <TabComponent
          style={style}
          props={component.props}
          key={id}
          params={params}
        />
      );
    case "downArrow":
      return (
        <ArrowDownCustomComponent
          style={style}
          props={component.props}
          key={id}
        />
      );
    case "shopNowButton":
      return <ShopNowButtonComponent style={style} key={id} />;
    case "accordion":
      return (
        <AccordionComponent
          style={style}
          props={component.props}
          params={params}
          key={id}
        />
      );
    case "googleMapsEmbed":
      return (
        <GoogleMapsEmbedComponent
          style={style}
          props={component.props}
          key={id}
        />
      );
    case "boutiqueIntro":
      return <BoutiqueIntroComponent props={component.props} key={id} />;
    case "boutiqueTransition":
      return <BoutqiueTransitionComponent props={component.props} key={id} />;

    case "default":
      return (
        <div className={style} key={id}>
          {children && renderChildren(children, params, isClient)}
        </div>
      );
    case "customCarousel":
      return (
        <CustomCarouselWrapper
          style={style}
          props={component.props}
          params={params}
          key={id}
        />
      );
    case "complexForm":
      return (
        <ComplexFormComponent
          style={style}
          props={component.props}
          formChildren={children || []}
          params={params}
          key={id}
        />
      );
    case "input":
      return <InputComponent style={style} props={component.props} key={id} />;
    case "label":
      return <LabelComponent style={style} props={component.props} key={id} />;
    case "submitButton":
      return (
        <SubmitButtonComponent style={style} props={component.props} key={id} />
      );
    case "cardCarousel":
      return isClient ? (
        <ProductCardCarouselWrapperClient
          style={style}
          props={component.props}
          params={params}
          key={id}
        />
      ) : (
        <CardCarouselComponent
          style={style}
          props={component.props}
          params={params}
          key={id}
        />
      );
    case "questionnaire":
      return (
        <QuestionnaireComponent
          style={style}
          props={component.props}
          params={params}
          key={id}
        />
      );
    default:
      return null;
  }
}
